import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Chip, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip
} from '@mui/material';

import { 
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

interface ContractTransaction {
  id: string;
  contractId: string;
  userId: string;
  action: 'create' | 'update' | 'delete' | 'status_change' | 'extend';
  fieldChanged?: string;
  previousValue?: string;
  newValue?: string;
  notes?: string;
  transactionDate: string;
  contractType: 'vidines' | 'partneriai';
  contract: {
    id: string;
    pavadinimas: string;
    sutartiesNumeris: string;
    tipas: string;
  };
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface ContractLogSectionProps {
  contractType: 'vidines' | 'partneriai';
}

const ContractLogSection: React.FC<ContractLogSectionProps> = ({ contractType }) => {
  const { token, hasPermission } = useAuth();
  const [transactions, setTransactions] = useState<ContractTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearPassword, setClearPassword] = useState('');
  const [clearError, setClearError] = useState<string | null>(null);
  const [clearSubmitting, setClearSubmitting] = useState(false);
  const [clearSuccess, setClearSuccess] = useState(false);

  const canClearLog = hasPermission('contracts.delete');

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const offset = (page - 1) * limit;
      let url = `${getApiUrl('/contracts/transactions')}?limit=${limit}&offset=${offset}&tipas=${contractType}`;
      
      if (actionFilter !== 'all') {
        url += `&action=${actionFilter}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch contract transactions');
      }

      const data = await response.json();
      setTransactions(data.transactions);
      setTotal(data.total ?? 0);
    } catch (err) {
      console.error('Error fetching contract transactions:', err);
      setError('Failed to load transaction log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded) {
      fetchTransactions();
    }
  }, [expanded, actionFilter, contractType, page, limit]);

  const getActionColor = (action: string) => {
    switch (action) {
      case 'create': return 'success';
      case 'update': return 'primary';
      case 'delete': return 'error';
      case 'status_change': return 'warning';
      case 'extend': return 'info';
      default: return 'default';
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'create': return 'Sukurta';
      case 'update': return 'Atnaujinta';
      case 'delete': return 'Ištrinta';
      case 'status_change': return 'Statusas pakeistas';
      case 'extend': return 'Pratęsta';
      default: return action;
    }
  };

  const formatTransactionDetails = (transaction: ContractTransaction) => {
    if (transaction.action === 'create') {
      return transaction.notes || 'Sutartis sukurta';
    }
    
    if (transaction.action === 'delete') {
      return transaction.notes || 'Sutartis ištrinta';
    }
    
    if (transaction.action === 'status_change') {
      return (
        transaction.notes ||
        `Statusas pakeistas iš '${transaction.previousValue || '—'}' į '${transaction.newValue || '—'}'`
      );
    }

    if (transaction.action === 'update' && transaction.fieldChanged) {
      return `Laukas '${transaction.fieldChanged}' pakeistas iš '${transaction.previousValue}' į '${transaction.newValue}'`;
    }

    return transaction.notes || 'Sutartis atnaujinta';
  };

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleOpenClearDialog = () => {
    setClearDialogOpen(true);
    setClearPassword('');
    setClearError(null);
    setClearSuccess(false);
  };

  const handleCloseClearDialog = () => {
    setClearDialogOpen(false);
    setClearPassword('');
    setClearError(null);
  };

  const handleClearSubmit = async () => {
    try {
      setClearSubmitting(true);
      setClearError(null);
      const response = await fetch(getApiUrl('/contracts/transactions/clear'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tipas: contractType, password: clearPassword })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 403) {
          setClearError(data.error || 'Neteisingas slaptažodis');
          return;
        }
        setClearError(data.error || 'Nepavyko išvalyti žurnalo');
        return;
      }
      setClearSuccess(true);
      setClearPassword('');
      setPage(1);
      fetchTransactions();
      setTimeout(() => {
        setClearDialogOpen(false);
        setClearSuccess(false);
      }, 1500);
    } catch (err) {
      console.error('Error clearing contract log:', err);
      setClearError('Nepavyko išvalyti žurnalo');
    } finally {
      setClearSubmitting(false);
    }
  };

  const typeLabel = contractType === 'vidines' ? 'Vidinės sutartys' : 'Partneriai';

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Veiksmų Žurnalas ({typeLabel})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {canClearLog && (
              <Tooltip title="Išvalyti žurnalą">
                <IconButton color="error" size="small" onClick={handleOpenClearDialog} aria-label="Išvalyti žurnalą">
                  <DeleteSweepIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleToggleExpanded}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Veiksmo tipas</InputLabel>
                <Select
                  value={actionFilter}
                  label="Veiksmo tipas"
                  onChange={(e) => {
                    setActionFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">Visi veiksmai</MenuItem>
                  <MenuItem value="create">Sukurta</MenuItem>
                  <MenuItem value="update">Atnaujinta</MenuItem>
                  <MenuItem value="delete">Ištrinta</MenuItem>
                  <MenuItem value="status_change">Statusas pakeistas</MenuItem>
                  <MenuItem value="extend">Pratęsta</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Rodoma</InputLabel>
                <Select
                  value={limit}
                  label="Rodoma"
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  sx={{ minWidth: 36 }}
                >
                  Ankstesnis
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  {total > 0
                    ? `Rodoma ${(page - 1) * limit + 1}–${Math.min(page * limit, total)} iš ${total}`
                    : '—'}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={total === 0 || page * limit >= total}
                  onClick={() => setPage((p) => p + 1)}
                  sx={{ minWidth: 36 }}
                >
                  Kitas
                </Button>
              </Box>
            </Box>

            {/* Transactions Table */}
            {loading ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Veiksmas</TableCell>
                      <TableCell>Sutartis</TableCell>
                      <TableCell>Detalės</TableCell>
                      <TableCell>Vykdytojas</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {new Date(transaction.transactionDate).toLocaleString('lt-LT')}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getActionLabel(transaction.action)}
                            color={getActionColor(transaction.action) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {transaction.contract.pavadinimas}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {transaction.contract.sutartiesNumeris}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTransactionDetails(transaction)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {transaction.user.firstName} {transaction.user.lastName}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {transactions.length === 0 && !loading && (
              <Box textAlign="center" py={2}>
                <Typography variant="body2" color="text.secondary">
                  Veiksmų nerasta
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>

      <Dialog open={clearDialogOpen} onClose={handleCloseClearDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Išvalyti žurnalą</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Visi šio tipo ({typeLabel}) veiksmų žurnalo įrašai bus visam laikui ištrinti. Šio veiksmo atšaukti negalima.
          </Typography>
          {clearError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setClearError(null)}>
              {clearError}
            </Alert>
          )}
          {clearSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Žurnalas sėkmingai išvalytas.
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Patvirtinimo slaptažodis"
            type="password"
            fullWidth
            value={clearPassword}
            onChange={(e) => setClearPassword(e.target.value)}
            disabled={clearSubmitting}
            onKeyDown={(e) => e.key === 'Enter' && handleClearSubmit()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClearDialog} disabled={clearSubmitting}>
            Atšaukti
          </Button>
          <Button onClick={handleClearSubmit} color="error" variant="contained" disabled={clearSubmitting || !clearPassword.trim()}>
            {clearSubmitting ? 'Išvaloma…' : 'Išvalyti žurnalą'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ContractLogSection;


